import React, {useState} from "react";
import {useMutation} from "@tanstack/react-query";
import {FaDownload, FaRedo, FaRegCopy} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import WaveformAudioPlayer from "../../../../../components/audio-player/waveform-audio-player.jsx";
import Button from "../../../../../components/button/button.jsx";
import AttendanceService from "../../../../../services/dispatch/attendance-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import AiTranscription from "../../../../../components/icons/ai-transcription.jsx";
import AiSummary from "../../../../../components/icons/ai-summary.jsx";

const SUMMARY = "summarized_audio";
const TRANSCRIPTION = "transcribed_audio";

const BUTTON_LABEL = {
    [SUMMARY]: "Resumir com IA",
    [TRANSCRIPTION]: "Transcrição completa",
};

const MODAL_TITLE = {
    [SUMMARY]: "Resumo da gravação",
    [TRANSCRIPTION]: "Transcrição completa",
};

const AttendanceCallCard = ({ramalIdentify, callFileUrl, onChangeField, form, setIsLoading}) => {
    const [target, setTarget] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    const summarization = useMutation({
        mutationFn: async () => {
            const data = await AttendanceService.audioSummarization(callFileUrl);
            if (!data?.[SUMMARY] && !data?.[TRANSCRIPTION]) throw new Error("Empty audio summarization response");

            return data;
        },
        onMutate: () => setIsLoading(true),
        onError: (error) => {
            console.error(error);

            setTarget(null);
            setOpenModal(false);
            projectUtils.swalErrorMessage("", "Não foi possível processar a gravação com IA.", "Ok");
        },
        onSettled: () => setIsLoading(false),
    });

    const savedTranscription = form.audioTranscription ?? "";

    const runSummarization = (modal, {adoptTranscription = false} = {}) => {
        summarization.mutate(undefined, {
            onSuccess: (data) => {
                if (adoptTranscription || !savedTranscription) onChangeField("audioTranscription", data[TRANSCRIPTION] ?? "");

                setTarget(modal);
                setOpenModal(true);
            }
        });
    };

    const open = (modal) => {
        if (summarization.isPending) return;

        const alreadyLoaded = modal === TRANSCRIPTION ? Boolean(savedTranscription) : Boolean(summarization.data);

        if (alreadyLoaded) {
            setTarget(modal);
            setOpenModal(true);
            return;
        }

        runSummarization(modal, {adoptTranscription: modal === TRANSCRIPTION});
    };

    const regenerate = () => runSummarization(target, {adoptTranscription: target === TRANSCRIPTION});

    const content = target === TRANSCRIPTION ? savedTranscription : summarization.data?.[SUMMARY];

    const renderContent = () => {
        if (!content) return <div className="p-4 text-[#68727f]">Conteúdo indisponível para esta gravação.</div>;

        return <div className="max-h-[60vh] overflow-auto p-1 leading-[1.6] whitespace-pre-line">{content}</div>;
    };

    const copyToTranscription = (content) => {
        const batata = "Resumo da gravação de chamada telefônica gerado por IA:\n\n" + content;
        const transcription = [form.transcription, batata].filter(Boolean).join(form.transcription ? "\n\n" : "");

        onChangeField("transcription", transcription);
        setTarget(null);
        setOpenModal(false);
    }

    if (!ramalIdentify) return null;

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title className="flex items-center gap-2">
                        Gravação de chamada telefônica
                    </Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className="text-sm text-gray-600 space-y-3">
                        <p className={"w-full"}>Atendimento criado automaticamente por {ramalIdentify}</p>
                        {callFileUrl && (
                            <>
                                <section className={"flex justify-between gap-4 items-center"}>
                                    <WaveformAudioPlayer className="w-full" src={callFileUrl}/>

                                    <div className={"flex gap-2"}>
                                        <Button target="primary-outline" title="Baixar chamada telefônica" onClick={() => window.open(callFileUrl, "_blank")}>
                                            <FaDownload/>
                                        </Button>
                                        <Button className={"min-w-45"} onClick={() => open(SUMMARY)}>
                                            <AiSummary />
                                            {BUTTON_LABEL[SUMMARY]}
                                        </Button>

                                        <Button className={"min-w-50"} target={"primary-outline"} onClick={() => open(TRANSCRIPTION)}>
                                            <AiTranscription />
                                            {BUTTON_LABEL[TRANSCRIPTION]}
                                        </Button>
                                    </div>
                                </section>
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Modal open={openModal} setOpen={() => setOpenModal(false)} title={MODAL_TITLE[target] ?? ""} className={"w-6/12"}>
                <Modal.Body>
                    {renderContent()}
                </Modal.Body>

                {Boolean(content) && target === SUMMARY && (
                    <Modal.Footer>
                        <Button onClick={() => copyToTranscription(content)} className={"items-center gap-1.5"}>
                            <FaRegCopy/> Copiar resumo
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </>
    );
};

export default AttendanceCallCard;
